import { useState } from 'react'
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Layout,
  Text,
  Link,
} from '@shopify/polaris'
import toast from 'react-hot-toast'

import { workspaceApi } from '@/services/api'

/**
 * Self-serve signup for the STANDALONE deployment: creates a workspace and its
 * first OWNER account, then bootstraps the session via a full reload (so /auth/me
 * loads stores + capabilities).
 */
export default function WorkspaceSignupPage() {
  const [formData, setFormData] = useState({
    workspaceName: '',
    ownerName: '',
    email: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await workspaceApi.createWorkspace(formData)
      const { token } = response.data.data
      localStorage.setItem('auth_token', token)
      toast.success('Workspace created!')
      // Full reload so AuthProvider hydrates from /auth/me (stores + capabilities).
      window.location.href = '/dashboard'
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Failed to create workspace'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const canSubmit =
    formData.workspaceName &&
    formData.ownerName &&
    formData.email &&
    formData.password.length >= 8

  return (
    <Page>
      <Layout>
        <Layout.Section>
          <div style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '80px' }}>
            <Card>
              <div style={{ padding: '24px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <Text variant="headingLg" as="h1">
                    Create your workspace
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Manage your team's WhatsApp conversations in one place
                  </Text>
                </div>

                <form onSubmit={handleSubmit}>
                  <FormLayout>
                    <TextField
                      label="Workspace name"
                      value={formData.workspaceName}
                      onChange={handleInputChange('workspaceName')}
                      placeholder="e.g. Acme Sales"
                      autoComplete="organization"
                      required
                    />

                    <TextField
                      label="Your name"
                      value={formData.ownerName}
                      onChange={handleInputChange('ownerName')}
                      placeholder="Enter your full name"
                      autoComplete="name"
                      required
                    />

                    <TextField
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      placeholder="Enter your email"
                      autoComplete="email"
                      required
                    />

                    <TextField
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      required
                    />

                    <Button submit primary fullWidth loading={loading} disabled={!canSubmit}>
                      Create workspace
                    </Button>
                  </FormLayout>
                </form>

                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '24px',
                    paddingTop: '24px',
                    borderTop: '1px solid #e1e3e5',
                  }}
                >
                  <Text variant="bodyMd" as="p" color="subdued">
                    Already have an account? <Link url="/login">Sign in</Link>
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
